import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
	Box,
	Typography,
	Button,
	Grid,
	CircularProgress,
	Alert,
	Link,
	Divider,
	IconButton,
	TextField,
	InputAdornment,
	Paper,
	List,
	ListItemButton,
	ListItemText,
} from "@mui/material";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { useGrade } from "../../../contexts/GradeContext";
import {
	CARD_SURFACE,
	TOPIC_TILE_PALETTE,
} from "../../../components/teacher/module/teacherModuleStyles";
import { getCurriculumTopicVisual } from "../../../components/teacher/module/topicVisuals";

/** Strip basic HTML and collapse whitespace for matching CMS text fields. */
const normalizeForSearch = (text) =>
	String(text ?? "")
		.replace(/<[^>]*>/g, " ")
		.replace(/&nbsp;/gi, " ")
		.replace(/\s+/g, " ")
		.trim()
		.toLowerCase();

/**
 * After choosing a grade, pick a curriculum topic (Addition, Numbers, …) before games.
 */
const TeacherTopics = () => {
	const { gradeId } = useParams();
	const { user, supabase } = useAuth();
	const { updateSelectedGrade } = useGrade();
	const navigate = useNavigate();

	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [gradeName, setGradeName] = useState("");
	const [topicRows, setTopicRows] = useState([]);
	const [allGames, setAllGames] = useState([]);
	const [gameSearch, setGameSearch] = useState("");

	const load = useCallback(async () => {
		if (!user?.id || !gradeId) return;
		setLoading(true);
		setError(null);
		try {
			const { data: allowed, error: aErr } = await supabase
				.from("teacher_grades")
				.select("id")
				.eq("profile_id", user.id)
				.eq("grade_id", gradeId)
				.maybeSingle();

			if (aErr) throw aErr;
			if (!allowed) {
				setError("You don't have access to this grade.");
				setTopicRows([]);
				setAllGames([]);
				return;
			}

			updateSelectedGrade(gradeId);

			const { data: gradeRow, error: gErr } = await supabase
				.from("grades")
				.select("name")
				.eq("id", gradeId)
				.single();

			if (gErr) throw gErr;
			setGradeName(gradeRow?.name || "Your class");

			const { data: cgRows, error: cgErr } = await supabase
				.from("category_grades")
				.select(
					`
					category_id,
					course_categories (
						id,
						name,
						concept,
						description,
						playorder,
						status,
						topic_id
					)
				`
				)
				.eq("grade_id", gradeId);

			if (cgErr) throw cgErr;

			const topicIds = new Set();
			let hasUnassigned = false;
			(cgRows || []).forEach((r) => {
				const c = r.course_categories;
				if (!c || c.status === false) return;
				if (c.topic_id) topicIds.add(c.topic_id);
				else hasUnassigned = true;
			});

			let topics = [];
			if (topicIds.size > 0) {
				const { data: tRows, error: tErr } = await supabase
					.from("funmath_topics")
					.select("id, name, sort_order, status")
					.in("id", [...topicIds])
					.eq("status", true)
					.order("sort_order", { ascending: true })
					.order("name", { ascending: true });

				if (tErr) throw tErr;
				topics = tRows || [];
			}

			const topicNameById = new Map(
				(topics || []).map((t) => [t.id, t.name])
			);

			const byCategoryId = new Map();
			(cgRows || []).forEach((r) => {
				const c = r.course_categories;
				if (!c || c.status === false || !c.id) return;
				if (!byCategoryId.has(c.id)) byCategoryId.set(c.id, c);
			});

			const gradeLabel = gradeRow?.name || "";

			setAllGames(
				[...byCategoryId.values()].map((c) => ({
					id: c.id,
					name: c.name?.trim() || "Game",
					concept: c.concept || "",
					description: c.description || "",
					playorder: c.playorder,
					topicName: c.topic_id
						? topicNameById.get(c.topic_id) || "Topic"
						: "Other games",
					gradeLabel,
				}))
			);

			const rows = topics.map((t) => ({ kind: "topic", id: t.id, name: t.name }));
			if (hasUnassigned) {
				rows.push({ kind: "other", id: "other", name: "Other games" });
			}

			setTopicRows(rows);
		} catch (e) {
			console.error(e);
			setError(e.message || "Failed to load topics");
			setTopicRows([]);
			setAllGames([]);
		} finally {
			setLoading(false);
		}
	}, [user?.id, gradeId, supabase, updateSelectedGrade]);

	useEffect(() => {
		load();
	}, [load]);

	const handleTopic = (row) => {
		if (row.kind === "other") {
			navigate(`/teacher/games/${gradeId}/other`);
		} else {
			navigate(`/teacher/games/${gradeId}/${row.id}`);
		}
	};

	const filteredGames = useMemo(() => {
		const q = normalizeForSearch(gameSearch);
		if (!q || allGames.length === 0) return [];
		return allGames
			.filter((g) => {
				const hay = normalizeForSearch(
					[
						g.name,
						g.concept,
						g.description,
						g.topicName,
						g.gradeLabel,
						g.playorder != null ? String(g.playorder) : "",
					].join(" ")
				);
				return hay.includes(q);
			})
			.slice(0, 40);
	}, [allGames, gameSearch]);

	const handleGameFromSearch = (categoryId) => {
		setGameSearch("");
		navigate(`/teacher/videos/${gradeId}/${categoryId}`);
	};

	const goChangeGrade = () => navigate("/dashboard");

	if (loading) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
				<CircularProgress size={36} sx={{ color: "#16a34a" }} />
			</Box>
		);
	}

	return (
		<Box>
			{error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}

			<Box sx={{ ...CARD_SURFACE, p: { xs: 2, sm: 2.5 } }}>
				<Box
					sx={{
						display: "flex",
						flexDirection: { xs: "column", sm: "row" },
						alignItems: { xs: "stretch", sm: "center" },
						justifyContent: "space-between",
						gap: { xs: 1.5, sm: 2 },
						mb: 2.5,
						pb: 2,
						borderBottom: "1px solid",
						borderColor: "divider",
					}}>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 0.5,
							minWidth: 0,
						}}>
						<IconButton
							aria-label="Back to dashboard"
							onClick={goChangeGrade}
							size="small"
							sx={{
								color: "#1e3a5f",
								flexShrink: 0,
								"&:hover": { backgroundColor: "rgba(30, 58, 95, 0.08)" },
							}}>
							<ChevronLeft size={22} strokeWidth={2.5} />
						</IconButton>
						<Typography
							sx={{
								fontWeight: 800,
								color: "#1e3a5f",
								fontSize: { xs: "1.25rem", sm: "1.35rem" },
							}}>
							Choose a topic
						</Typography>
					</Box>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: { xs: "flex-start", sm: "flex-end" },
							gap: 1,
							flexWrap: "wrap",
						}}>
						<Typography
							variant="body2"
							sx={{ color: "text.secondary", fontWeight: 600 }}>
							{gradeName}
						</Typography>
						<Divider
							orientation="vertical"
							flexItem
							sx={{ borderColor: "divider", alignSelf: "stretch", my: 0.25 }}
						/>
						<Link
							component="button"
							type="button"
							variant="body2"
							onClick={goChangeGrade}
							underline="hover"
							sx={{
								cursor: "pointer",
								fontWeight: 600,
								display: "inline-flex",
								alignItems: "center",
								gap: 0.25,
							}}>
							Change grade
							<ChevronRight size={16} />
						</Link>
					</Box>
				</Box>

				{allGames.length > 0 && (
					<Box sx={{ mb: 2.5 }}>
						<TextField
							fullWidth
							size="small"
							placeholder="Search games by name, topic, concept, description, class, or play order…"
							value={gameSearch}
							onChange={(e) => setGameSearch(e.target.value)}
							autoComplete="off"
							inputProps={{
								"aria-label": "Search games",
							}}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<Search
											size={18}
											strokeWidth={2.25}
											color="#64748b"
											aria-hidden
										/>
									</InputAdornment>
								),
							}}
							sx={{
								"& .MuiOutlinedInput-root": {
									borderRadius: 2,
									backgroundColor: "#f8fafc",
								},
							}}
						/>
						{gameSearch.trim() && filteredGames.length > 0 && (
							<Paper
								elevation={3}
								sx={{
									mt: 1,
									maxHeight: 280,
									overflow: "auto",
									borderRadius: 2,
									border: "1px solid",
									borderColor: "divider",
								}}>
								<List dense disablePadding>
									{filteredGames.map((g) => (
										<ListItemButton
											key={g.id}
											onClick={() => handleGameFromSearch(g.id)}
											sx={{
												alignItems: "flex-start",
												py: 1.25,
												px: 1.5,
											}}>
											<ListItemText
												primary={g.name}
												secondary={
													[g.topicName, g.concept?.trim()]
														.filter(Boolean)
														.join(" · ") || null
												}
												primaryTypographyProps={{
													fontWeight: 700,
													color: "#1e3a5f",
													fontSize: "0.95rem",
												}}
												secondaryTypographyProps={{
													variant: "caption",
													fontWeight: 600,
													sx: {
														display: "-webkit-box",
														WebkitLineClamp: 2,
														WebkitBoxOrient: "vertical",
														overflow: "hidden",
													},
												}}
											/>
										</ListItemButton>
									))}
								</List>
							</Paper>
						)}
						{gameSearch.trim() &&
							filteredGames.length === 0 &&
							allGames.length > 0 && (
								<Typography
									variant="body2"
									color="text.secondary"
									sx={{ mt: 1, ml: 0.5 }}>
									No games match your search.
								</Typography>
							)}
					</Box>
				)}

				{topicRows.length === 0 && !error ? (
					<Typography color="text.secondary" align="center">
						No topics are available for this grade yet. Ask your admin to assign
						topics to games in Manage Games.
					</Typography>
				) : (
					<Grid container spacing={1.5}>
						{topicRows.map((row, index) => {
							const { Icon, label } = getCurriculumTopicVisual(row);
							const pal =
								TOPIC_TILE_PALETTE[index % TOPIC_TILE_PALETTE.length];
							return (
								<Grid item xs={6} key={row.kind === "other" ? "other" : row.id}>
									<Button
										fullWidth
										onClick={() => handleTopic(row)}
										sx={{
											py: 1.75,
											px: 1.5,
											minHeight: 88,
											borderRadius: 2.5,
											justifyContent: "flex-start",
											textTransform: "none",
											color: "#fff",
											backgroundColor: pal.bg,
											boxShadow: `0 5px 0 ${pal.shadow}, 0 8px 20px rgba(0,0,0,0.12)`,
											"&:hover": {
												backgroundColor: pal.bg,
												filter: "brightness(1.06)",
												boxShadow: `0 3px 0 ${pal.shadow}, 0 6px 16px rgba(0,0,0,0.15)`,
												transform: "translateY(2px)",
											},
										}}>
										<Icon
											size={28}
											strokeWidth={2.5}
											style={{ flexShrink: 0, marginRight: 10 }}
										/>
										<Typography
											sx={{
												fontWeight: 800,
												fontSize: "0.95rem",
												textAlign: "left",
												lineHeight: 1.25,
											}}>
											{label}
										</Typography>
									</Button>
								</Grid>
							);
						})}
					</Grid>
				)}
			</Box>

			<Box
				sx={{
					mt: 3,
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					gap: 1.25,
					py: 1,
				}}>
				<img
					src="/logo.png"
					alt="Funmiyo"
					style={{ height: 40, width: "auto", objectFit: "contain" }}
				/>
			</Box>
		</Box>
	);
};

export default TeacherTopics;
